/**
 * DigitalRingSystem — Northstar Stage 3.5.
 *
 * Art-direction pass: the rings must read as fragments of a larger
 * digital environment, not as five perfect circles. To achieve that:
 *
 *   - Every ring is now defined as one or more arcs rather than a
 *     closed circle. Arcs vary in length, opacity, and position so
 *     the eye never resolves them as concentric targets.
 *   - Extra orphan arc fragments sit outside R5 to imply that the
 *     ring architecture continues beyond the viewport.
 *   - The subtle rotations stay so the environment feels alive.
 *   - Segmentation of R2 stays deliberately irregular.
 *
 * All values still derive from `identity` (photo diameter) so the
 * whole environment scales as one unit and pairs with the atmosphere
 * and connection network.
 */
import React from "react";

const GREEN = "126,212,147";
const MINT = "184,239,198";

const RING_SCALE = { R1: 1.72, R2: 2.29, R3: 3.0, R4: 3.86, R5: 5.14 };

const RINGS_FOR_TIER = {
  "desktop-lg": ["R1", "R2", "R3", "R4", "R5", "OUTER"],
  desktop:      ["R1", "R2", "R3", "R4", "R5", "OUTER"],
  tablet:       ["R1", "R2", "R3", "R4", "OUTER"],
  narrow:       ["R1", "R4"],
  mobile:       ["R1"],
};

// R1 is now two long arcs with breaks at top and bottom — no
// complete inner circle behind the portrait.
const R1_ARCS = [
  [16, 168],
  [196, 348],
];

// R2 segmented, irregular arc lengths — anti clock-face.
const R2_ARCS = [
  [6, 74],
  [88, 130],
  [154, 188],
  [208, 276],
  [292, 336],
];

// R3 arcs — long low-alpha with a break.
const R3_ARCS = [
  [10, 172],
  [186, 356],
];

// R4 has one wide arc + one small orphan.
const R4_ARCS = [
  [24, 202],
  [232, 340],
];

// R5 outermost — single arc with a break at the top.
const R5_ARC = [10, 350];

// Orphan outer arc fragments — deliberately go beyond R5 so the eye
// perceives the architecture extending off-viewport.
const OUTER_ARCS = [
  { r: 6.2, arc: [30, 92],  a: 0.09 },
  { r: 5.8, arc: [138, 168], a: 0.07 },
  { r: 6.5, arc: [212, 258], a: 0.08 },
  { r: 5.9, arc: [292, 334], a: 0.10 },
];

export default function DigitalRingSystem({
  identity = 260,
  tier = "desktop-lg",
  reducedMotion = false,
}) {
  const photoR = identity / 2;
  const outerR = photoR * 6.6 + 8;
  const size = outerR * 2 + 40;
  const cx = size / 2;
  const cy = size / 2;
  const active = RINGS_FOR_TIER[tier] || RINGS_FOR_TIER["desktop-lg"];
  const r = (k) => photoR * RING_SCALE[k];

  return (
    <div
      aria-hidden="true"
      data-testid="northstar-ring-system"
      data-tier={tier}
      className="absolute left-1/2 top-1/2 pointer-events-none"
      style={{
        transform: "translate(-50%, -50%)",
        width: size,
        height: size,
        zIndex: 3,
      }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* R1 — closest, two long arcs with breaks (no closed ring
             behind the portrait). */}
        {active.includes("R1") &&
          R1_ARCS.map(([a, b], i) => (
            <path
              key={`R1-${i}`}
              d={arcPath(cx, cy, r("R1"), a, b)}
              fill="none"
              stroke={`rgba(${GREEN},${0.48 - i * 0.06})`}
              strokeWidth={1}
              strokeLinecap="round"
              data-ring="R1"
            />
          ))}

        {/* R2 — irregular segmented + subtle radial ticks. Rotates. */}
        {active.includes("R2") && (
          <g
            data-ring="R2"
            style={{
              transformOrigin: `${cx}px ${cy}px`,
              transformBox: "view-box",
              animation: reducedMotion
                ? "none"
                : "northstar-ring-rot-ccw 60s linear infinite",
            }}
          >
            {R2_ARCS.map(([a, b], i) => (
              <path
                key={`R2-arc-${i}`}
                d={arcPath(cx, cy, r("R2"), a, b)}
                fill="none"
                stroke={`rgba(${GREEN},${0.28 + (i % 3) * 0.06})`}
                strokeWidth={0.9}
                strokeLinecap="round"
              />
            ))}
            {Array.from({ length: 24 }).map((_, i) => {
              const rad = (i * 15 * Math.PI) / 180;
              const inner = r("R2") + 3;
              const outer = r("R2") + 8;
              return (
                <line
                  key={`tick-${i}`}
                  x1={cx + Math.cos(rad) * inner}
                  y1={cy + Math.sin(rad) * inner}
                  x2={cx + Math.cos(rad) * outer}
                  y2={cy + Math.sin(rad) * outer}
                  stroke={`rgba(${MINT},0.16)`}
                  strokeWidth={0.5}
                  strokeLinecap="round"
                />
              );
            })}
          </g>
        )}

        {/* R3 — two long dashed arcs, slow clockwise rotation. */}
        {active.includes("R3") && (
          <g
            data-ring="R3"
            style={{
              transformOrigin: `${cx}px ${cy}px`,
              transformBox: "view-box",
              animation: reducedMotion
                ? "none"
                : "northstar-ring-rot-cw 120s linear infinite",
            }}
          >
            {R3_ARCS.map(([a, b], i) => (
              <path
                key={`R3-${i}`}
                d={arcPath(cx, cy, r("R3"), a, b)}
                fill="none"
                stroke={`rgba(${GREEN},${0.22 - i * 0.04})`}
                strokeWidth={0.75}
                strokeDasharray="3 6 9 5 2 8 4 6"
              />
            ))}
          </g>
        )}

        {/* R4 — wide breathing arc + orphan segment. */}
        {active.includes("R4") &&
          R4_ARCS.map(([a, b], i) => (
            <path
              key={`R4-${i}`}
              d={arcPath(cx, cy, r("R4"), a, b)}
              fill="none"
              stroke={`rgba(${GREEN},${0.14 - i * 0.04})`}
              strokeWidth={0.6}
              data-ring="R4"
              style={
                i === 0
                  ? {
                      animation: reducedMotion
                        ? "none"
                        : "northstar-ring-breath 8s ease-in-out infinite",
                    }
                  : undefined
              }
            />
          ))}

        {/* R5 — outermost, faint, INCOMPLETE (small break at top). */}
        {active.includes("R5") && (
          <path
            d={arcPath(cx, cy, r("R5"), R5_ARC[0], R5_ARC[1])}
            fill="none"
            stroke={`rgba(${GREEN},0.08)`}
            strokeWidth={0.5}
            data-ring="R5"
          />
        )}

        {/* Outer orphan fragments — suggest architecture extending
             beyond the viewport. */}
        {active.includes("OUTER") &&
          OUTER_ARCS.map((f, i) => (
            <path
              key={`OUTER-${i}`}
              d={arcPath(cx, cy, photoR * f.r, f.arc[0], f.arc[1])}
              fill="none"
              stroke={`rgba(${GREEN},${f.a})`}
              strokeWidth={0.5}
              strokeLinecap="round"
              data-ring="OUTER"
            />
          ))}
      </svg>

      <style>{`
        @keyframes northstar-ring-rot-ccw {
          from { transform: rotate(0deg); }
          to   { transform: rotate(-360deg); }
        }
        @keyframes northstar-ring-rot-cw {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes northstar-ring-breath {
          0%, 100% { opacity: 0.55; }
          50%      { opacity: 1;    }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="northstar-ring-system"] * { animation: none !important; }
        }
      `}</style>
    </div>
  );
}

function arcPath(cx, cy, r, aDeg, bDeg) {
  const a = (aDeg * Math.PI) / 180;
  const b = (bDeg * Math.PI) / 180;
  const x1 = cx + Math.cos(a) * r;
  const y1 = cy + Math.sin(a) * r;
  const x2 = cx + Math.cos(b) * r;
  const y2 = cy + Math.sin(b) * r;
  const large = bDeg - aDeg > 180 ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
}
